import React, { useState } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useMutation } from 'react-query';
import { exams } from '../../../../api/exams';
import toast from 'react-hot-toast';

const DemographicsForm = ({survey, refetch, setOpen}: {survey: any, setOpen: any ,refetch: any}) => {
   
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const {isLoading, mutateAsync} = useMutation(exams.saveDemographics)
  const user = JSON.parse(sessionStorage.getItem('user') || "{}")
  // Sort demographic fields by sort order
  const sortedFields = survey?.assessment?.pipeline?.demographicFields?.length ? [...survey?.assessment?.pipeline?.demographicFields].sort(
    (a, b) => 
      a.client_campaign_cycle_pipeline_demographics.sort - 
      b.client_campaign_cycle_pipeline_demographics.sort
  ) : [];

  const handleInputChange = (fieldId: any, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [fieldId]: value
    }));
    // Clear error when user starts typing
    if (errors[fieldId]) {
      setErrors((prev: any) => ({
        ...prev,
        [fieldId]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};
    sortedFields.forEach((field: any) => {
      if (!formData[field.id] || formData[field.id].trim() === '') {
        newErrors[field.id] = `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    const payload = {
      pipeline_id: survey.assessment.pipeline.id,
      demographics: Object.entries(formData).map(([demography_id, value]) => ({
        demography_id,
        value
      })) 
    };
    try {
      toast.loading('Loading...')
      const data = {
        userId: user?.user?.user_id,
        data: payload
      }
      const response = await mutateAsync(data);  
      toast.remove() 
      toast.success('Demographics saved successfully!')
      refetch()
      setOpen(false)
    } catch (error: any) {
        toast.remove()
        toast.error(error?.response?.data?.message || 'Error saving demographics')
    } finally {
      setIsSubmitting(false);
    }
  };

  const parseOptions = (optionsString: any) => {
    try {
      return JSON.parse(optionsString);
    } catch {
      return [];
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="bg-primary px-8 py-6">
        <h2 className="text-2xl font-bold text-white mb-2">
          Complete Your Demographics
        </h2>
        <p className="text-orange-50 text-sm">
          Please provide the following information to proceed with your survey
        </p>
      </div>

      {/* Form */}
      <div className="p-8">
        <div className="space-y-6">
          {sortedFields.map((field, index) => (
            <div key={field.id} className="relative">
              <label 
                htmlFor={field.id}
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                {field.label}
                <span className="text-red-500 ml-1">*</span>
              </label>

              {field.input_type === 'select' ? (
                <div className="relative">
                  <select
                    id={field.id}
                    value={formData[field.id] || ''}
                    onChange={(e) => handleInputChange(field.id, e.target.value)}
                    className={`w-full px-4 py-3 bg-white border-2 rounded-lg text-gray-700 
                      focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent
                      transition-all duration-200 appearance-none cursor-pointer
                      ${errors[field.id] 
                        ? 'border-red-300 bg-red-50' 
                        : formData[field.id]
                        ? 'border-primary bg-green-50'
                        : 'border-gray-300 hover:border-gray-400'
                      }`}
                  >
                    <option value="">Select {field.label}</option>
                    {parseOptions(field.options).map((option: any) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg 
                      className="w-5 h-5 text-gray-400" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M19 9l-7 7-7-7" 
                      />
                    </svg>
                  </div>
                </div>
              ) : (
                <input
                  type="text"
                  id={field.id}
                  value={formData[field.id] || ''}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  placeholder={`Enter your ${field.label.toLowerCase()}`}
                  className={`w-full px-4 py-3 border-2 rounded-lg text-gray-700 
                    focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent
                    transition-all duration-200
                    ${errors[field.id] 
                      ? 'border-red-300 bg-red-50' 
                      : formData[field.id]
                      ? 'border-primary bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                    }`}
                />
              )}

              {/* Error Message */}
              {errors[field.id] && (
                <div className="flex items-center gap-1 mt-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors[field.id]}</span>
                </div>
              )}

              {/* Success Indicator */}
              {!errors[field.id] && formData[field.id] && (
                <div className="absolute right-3 top-11 text-primary">
                  <CheckCircle className="w-5 h-5" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Progress Indicator */}
        <div className="mt-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">
              Completion Progress
            </span>
            <span className="text-sm font-semibold text-orange-600">
              {Math.round((Object.keys(formData).filter(key => formData[key]).length / sortedFields.length) * 100)}%
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 to-orange-600 transition-all duration-300"
              style={{ 
                width: `${(Object.keys(formData).filter(key => formData[key]).length / sortedFields.length) * 100}%` 
              }}
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-8 flex gap-4">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || submitSuccess}
            className={`flex-1 py-4 px-6 rounded-lg font-semibold text-white text-lg
              transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]
              focus:outline-none focus:ring-4 focus:ring-orange-300
              ${isSubmitting || submitSuccess
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary shadow-lg hover:shadow-xl'
              }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin" />
                <span>Submitting...</span>
              </div>
            ) : submitSuccess ? (
              <div className="flex items-center justify-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>Success!</span>
              </div>
            ) : (
              'Submit & Continue'
            )}
          </button>
        </div>

        {/* Helper Text */}
        <p className="mt-4 text-center text-md text-gray-500">
          All fields marked with <span className="text-red-500">*</span> are required
        </p>
      </div>
    </div>
  );
};

export default DemographicsForm;